import { Link, useLocation, useRouter } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { Logo } from "./Logo";
import { type Lang, LANGS } from "@/i18n";
import { SITE, detectLangFromPath, getPageKey, path } from "@/lib/site";
import { useNavMenu } from "@/lib/useNavMenu";
import { useHeaderCta } from "@/lib/useHeaderCta";

export function Header() {
  const location = useLocation();
  const router = useRouter();
  const { t, i18n } = useTranslation();
  const lang = detectLangFromPath(location.pathname);
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (i18n.language !== lang) i18n.changeLanguage(lang);
  }, [lang, i18n]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  const { data: navItems } = useNavMenu("header", lang);
  const { data: cta } = useHeaderCta(lang, t("nav.cta"));
  const ctaLabel = cta?.label ?? t("nav.cta");
  const ctaHref = cta?.href ?? path(lang, "contact");
  const ctaExternal = cta?.isExternal ?? false;
  const ctaNewTab = cta?.openInNewTab ?? false;
  const fallbackLinks = [
    { to: path(lang, "services"), label: t("nav.services"), external: false, newTab: false },
    { to: path(lang, "references"), label: t("nav.references"), external: false, newTab: false },
    { to: path(lang, "about"), label: t("nav.about"), external: false, newTab: false },
    { to: path(lang, "contact"), label: t("nav.contact"), external: false, newTab: false },
  ];
  const links =
    navItems && navItems.length > 0
      ? navItems
          .filter((i) => i.target !== "home")
          .map((i) => ({
            to: i.href,
            label: i.label,
            external: i.isExternal,
            newTab: i.openInNewTab,
          }))
      : fallbackLinks;

  const switchLang = (target: Lang) => {
    const page = getPageKey(location.pathname, lang);
    router.navigate({ to: path(target, page) });
  };

  return (
    <header
      className={`sticky top-0 z-50 transition-all duration-300 ${
        scrolled
          ? "border-b border-[var(--border)] bg-background/85 backdrop-blur"
          : "border-b border-transparent bg-background"
      }`}
    >
      <div className="container-page flex h-16 items-center justify-between gap-6 md:h-20">
        <Link to={path(lang, "home")} className="shrink-0">
          <Logo variant="full" priority />
        </Link>

        <nav
          className="hidden items-center gap-9 lg:flex"
          aria-label="Primary"
        >
          {links.map((l) =>
            l.external ? (
              <a
                key={l.to}
                href={l.to}
                target={l.newTab ? "_blank" : undefined}
                rel={l.newTab ? "noopener noreferrer" : undefined}
                className="nav-link font-display text-[13px] font-medium uppercase tracking-[0.12em] text-foreground/70 hover:text-foreground"
              >
                {l.label}
              </a>
            ) : (
              <Link
                key={l.to}
                to={l.to}
                target={l.newTab ? "_blank" : undefined}
                className="nav-link font-display text-[13px] font-medium uppercase tracking-[0.12em] text-foreground/70 hover:text-foreground"
                activeOptions={{ exact: true }}
              >
                {l.label}
              </Link>
            ),
          )}
        </nav>

        <div className="hidden items-center gap-4 lg:flex">
          <div
            className="lang-switch text-[11px] font-medium uppercase tracking-[0.18em]"
            role="group"
            aria-label="Language"
          >
            {LANGS.map((l) => (
              <button
                key={l}
                onClick={() => switchLang(l)}
                aria-current={l === lang ? "true" : undefined}
                aria-label={`Switch language to ${l.toUpperCase()}`}
              >
                {l}
              </button>
            ))}
          </div>
          {ctaExternal ? (
            <a
              href={ctaHref}
              target={ctaNewTab ? "_blank" : undefined}
              rel={ctaNewTab ? "noopener noreferrer" : undefined}
              className="btn-blaze cta-arrow inline-flex items-center gap-2 px-5 py-2.5 text-[12px] font-bold uppercase tracking-[0.12em]"
            >
              {ctaLabel} <ArrowUpRight size={14} />
            </a>
          ) : (
            <Link
              to={ctaHref}
              target={ctaNewTab ? "_blank" : undefined}
              className="btn-blaze cta-arrow inline-flex items-center gap-2 px-5 py-2.5 text-[12px] font-bold uppercase tracking-[0.12em]"
            >
              {ctaLabel} <ArrowUpRight size={14} />
            </Link>
          )}
        </div>

        <button
          className="burger grid h-11 w-11 place-items-center rounded-md border border-[var(--border-strong)] lg:hidden"
          aria-label={open ? "Close menu" : "Open menu"}
          aria-expanded={open}
          onClick={() => setOpen((v) => !v)}
        >
          {open ? <X size={18} /> : <Menu size={18} />}
        </button>
      </div>

      {open && (
        <div className="menu-drop border-t border-[var(--border)] bg-background lg:hidden">
          <div className="container-page flex flex-col gap-1 py-5">
            {links.map((l) =>
              l.external ? (
                <a
                  key={l.to}
                  href={l.to}
                  target={l.newTab ? "_blank" : undefined}
                  rel={l.newTab ? "noopener noreferrer" : undefined}
                  className="font-display rounded-md px-3 py-3 text-base uppercase tracking-[0.1em] text-foreground/80 transition-colors hover:bg-[var(--surface)]"
                >
                  {l.label}
                </a>
              ) : (
                <Link
                  key={l.to}
                  to={l.to}
                  target={l.newTab ? "_blank" : undefined}
                  className="font-display rounded-md px-3 py-3 text-base uppercase tracking-[0.1em] text-foreground/80 transition-colors hover:bg-[var(--surface)]"
                >
                  {l.label}
                </Link>
              ),
            )}
            <div className="mt-4 flex items-center justify-between gap-3 px-3">
              <div className="lang-switch text-[11px] font-medium uppercase tracking-[0.18em]">
                {LANGS.map((l) => (
                  <button
                    key={l}
                    onClick={() => switchLang(l)}
                    aria-current={l === lang ? "true" : undefined}
                    aria-label={`Switch language to ${l.toUpperCase()}`}
                  >
                    {l}
                  </button>
                ))}
              </div>
              {ctaExternal ? (
                <a
                  href={ctaHref}
                  target={ctaNewTab ? "_blank" : undefined}
                  rel={ctaNewTab ? "noopener noreferrer" : undefined}
                  className="btn-ink cta-arrow inline-flex items-center gap-2 rounded-full px-4 py-2 text-xs font-medium uppercase tracking-[0.1em]"
                >
                  {ctaLabel} <ArrowUpRight size={12} />
                </a>
              ) : (
                <Link
                  to={ctaHref}
                  target={ctaNewTab ? "_blank" : undefined}
                  className="btn-ink cta-arrow inline-flex items-center gap-2 rounded-full px-4 py-2 text-xs font-medium uppercase tracking-[0.1em]"
                >
                  {ctaLabel} <ArrowUpRight size={12} />
                </Link>
              )}
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
